/**
 * Read a backend error into something a component can render.
 *
 * The server already sends client-safe text (errors/generic_errors.ts on the backend owns the
 * copy), so this does NOT translate codes by default — it displays what arrived. ERROR_COPY is an
 * OVERRIDE table for the few places this app wants different wording, which means a new backend
 * error code renders correctly without a frontend release.
 */
export interface ApiError {
  /** Backend code ("CARD_NOT_FOUND"), or a client-side sentinel ("NETWORK" / "UNKNOWN"). */
  code: string;
  /** Display-ready text. Never axios's "Request failed with status code N". */
  message: string;
  /** HTTP status, or null when the request never reached the server. */
  status: number | null;
  /** Correlates with the backend log; worth showing on an unexpected failure. */
  requestUUID: string | null;
  /** True when the request never got a response — offline, DNS, CORS, timeout. */
  isNetwork: boolean;
}

export const NETWORK_CODE = "NETWORK";
export const UNKNOWN_CODE = "UNKNOWN";
/** Client-side sentinel: the reply arrived intact but apiClient could not decrypt it. */
export const DECRYPT_FAILED_CODE = "RESPONSE_DECRYPT_FAILED";

/**
 * Per-code wording overrides. Leave a code out to use the server's own text.
 * Only add an entry when this app genuinely needs to say something different.
 */
export const ERROR_COPY: Record<string, string> = {
  [NETWORK_CODE]: "You appear to be offline. Check your connection and retry.",

  // The call itself reached the server and was answered — only the reply was unreadable. The copy
  // must not claim the operation failed, or a user re-runs something that already went through.
  [DECRYPT_FAILED_CODE]:
    "We couldn't read the server's reply, so this may already have gone through. Please reload the page and check before trying again.",

  // Firebase Auth codes. mapFirebaseError preserves `error.code`, and the last branch of
  // parseApiError reads it, so these arrive here keyed exactly like a backend code. Without an
  // entry the user reads the raw SDK string ("Firebase: Error (auth/invalid-credential).").
  //
  // Both wrong-password codes are listed on purpose: with Email Enumeration Protection — the
  // default on current Firebase projects — reauthentication answers `invalid-credential`, and only
  // older projects still answer `wrong-password`.
  "auth/invalid-credential": "That current password isn't correct.",
  "auth/wrong-password": "That current password isn't correct.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a few minutes and try again.",
  "auth/weak-password": "Please choose a longer password.",
  "auth/requires-recent-login":
    "For your security, please sign in again and retry.",
  "auth/network-request-failed":
    "You appear to be offline. Check your connection and retry.",

  // Password-reset SEND codes. Under the current design Firebase hosts the reset page itself, so
  // auth/expired-action-code and auth/invalid-action-code never reach us — they are handled there.
  // Add them here only if the landing page is ever brought in-app.
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-disabled": "This account has been disabled. Please contact support.",
  // Authorized-Domains misconfiguration. Surfaces in a new environment before anywhere else, and
  // without copy it reads as an unexplained failure.
  "auth/unauthorized-continue-uri":
    "This app isn't set up correctly for password resets. Please contact support.",
  "auth/invalid-continue-uri":
    "This app isn't set up correctly for password resets. Please contact support.",
  "auth/missing-continue-uri":
    "This app isn't set up correctly for password resets. Please contact support.",
  // NOTE: auth/user-not-found is deliberately absent. Giving it distinct copy would turn the
  // forgot-password form into an account-existence oracle, so useForgotPassword swallows it into
  // the same neutral confirmation as success.

  // Firebase Auth codes. mapFirebaseError preserves `error.code`, and the last branch of
  // parseApiError reads it, so these arrive here keyed exactly like a backend code. Without an
  // entry the user reads the raw SDK string ("Firebase: Error (auth/invalid-credential).").
  //
  // Both wrong-password codes are listed on purpose: with Email Enumeration Protection — the
  // default on current Firebase projects — reauthentication answers `invalid-credential`, and only
  // older projects still answer `wrong-password`.
};

interface ServerErrorBody {
  code?: unknown;
  message?: unknown;
  error?: unknown;
  requestUUID?: unknown;
}

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

export function parseApiError(err: unknown, fallback: string): ApiError {
  const e = err as {
    response?: { status?: number; data?: unknown };
    request?: unknown;
    message?: unknown;
    code?: unknown;
  } | null;

  const response = e?.response;

  if (response) {
    const status = typeof response.status === "number" ? response.status : null;
    // A string body happens when a route sends text rather than JSON; an {iv,data} body means the
    // decrypt in apiClient could not run, in which case neither `code` nor `message` is present
    // and we fall through to the caller's fallback.
    const body: ServerErrorBody =
      typeof response.data === "object" && response.data !== null
        ? (response.data as ServerErrorBody)
        : {};

    const code = str(body.code) ?? UNKNOWN_CODE;
    // `error` covers the routes that still reply { error: "..." } — the sign-in rate limiter, and
    // anything not yet converted to respondWithError.
    const serverMessage = str(body.message) ?? str(body.error);

    return {
      code,
      message: ERROR_COPY[code] ?? serverMessage ?? fallback,
      status,
      requestUUID: str(body.requestUUID),
      isNetwork: false,
    };
  }

  // A request that went out but got nothing back. axios leaves `response` undefined and sets
  // `request`; err.message here is "Network Error" or "timeout of Nms exceeded", neither of which
  // is worth showing a user.
  if (e?.request) {
    return {
      code: NETWORK_CODE,
      message: ERROR_COPY[NETWORK_CODE] ?? fallback,
      status: null,
      requestUUID: null,
      isNetwork: true,
    };
  }

  // Not an axios failure at all — a Firebase SDK error, or a throw from our own code. Our own
  // throws carry a meaningful message and no matching code, so they pass through untouched; a
  // Firebase error carries `auth/...` and a message like "Firebase: Error (auth/invalid-credential).",
  // which is why ERROR_COPY is consulted here too and not only on the axios branch.
  const code = str(e?.code) ?? UNKNOWN_CODE;
  return {
    code,
    message: ERROR_COPY[code] ?? str(e?.message) ?? fallback,
    status: null,
    requestUUID: null,
    isNetwork: false,
  };
}

/** Shorthand for the common case: just the text to display. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  return parseApiError(err, fallback).message;
}
